import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

/**
 * Appointment Calendar Controller
 *
 * Handles all calendar view functionality for appointments:
 * - Week/month/year navigation
 * - Modal handling (open, close, load content)
 * - Add buttons in free spaces
 * - Auto-open modal from patient creation
 * - Auto-open invoice modal
 * - Tooltip initialization
 *
 * This controller is designed to be used alongside the main appointments_controller.js
 * for better separation of concerns.
 */
export default class extends Controller {
  // Constants for free space calculations
  static MIN_FREE_SPACE_HEIGHT = 15 // Minimum height percentage for free space detection
  static MIN_INTERACTIVE_SPACE_HEIGHT = 25 // Minimum height percentage for interactive add button
  static PATIENT_PRESELECTION_DELAY = 1000 // Delay in ms to wait for modal render before preselecting patient
  static SLOT_MERGE_TOLERANCE = 2 // Tolerance in percentage for merging adjacent slots
  static targets = [
    "monthSelector",
    "yearSelector",
    "weekSelector",
    "mainCalendarGrid"
  ]

  connect() {
    this.abortController = new AbortController()
    this.modalListeners = []
    this.initializeCalendar()
  }

  disconnect() {
    // Abort any pending fetch requests
    if (this.abortController) {
      this.abortController.abort()
    }

    // Clean up modal listeners
    this.cleanupModalListeners()

    // Close modal if open
    this.closeModal({ preventDefault: () => {}, stopPropagation: () => {} })
  }

  initializeCalendar() {
    // Initialize tooltips
    this.initializeTooltips()

    // Initialize add buttons in free spaces
    if (!this.addButtonsInitialized) {
      this.initializeAddButtons()
      this.addButtonsInitialized = true
    }

    // Handle auto-open modal from patient creation
    if (this.element.querySelector('[data-appointment-calendar-auto-open]')) {
      this.autoOpenModalFromPatientCreation()
    }

    // Handle auto-open invoice modal
    if (this.element.querySelector('[data-appointment-calendar-auto-open-invoice]')) {
      this.autoOpenInvoiceModal()
    }
  }

  // ==============================================
  // TOOLTIP INITIALIZATION
  // ==============================================

  initializeTooltips() {
    try {
      if (typeof $ !== 'undefined') {
        $('[data-toggle="tooltip"]').tooltip({
          container: 'body'
        })
      } else if (bootstrap && bootstrap.Tooltip) {
        const tooltips = document.querySelectorAll('[data-toggle="tooltip"]')
        tooltips.forEach(tooltip => {
          new bootstrap.Tooltip(tooltip)
        })
      }
    } catch (e) {
      console.error('Error initializing tooltips:', e)
    }
  }

  // ==============================================
  // NAVIGATION ACTIONS
  // ==============================================

  updateWeekSelector() {
    const year = parseInt(this.yearSelectorTarget.value)
    const month = parseInt(this.monthSelectorTarget.value)

    const startDate = new Date(year, month - 1, 1)
    const endDate = new Date(year, month, 0)

    const weeksSet = new Set()

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const weekNumber = this.getWeekNumber(d)
      weeksSet.add(weekNumber)
    }

    const weeks = Array.from(weeksSet).sort((a, b) => a - b)

    // Clear current selector
    this.weekSelectorTarget.innerHTML = ""

    weeks.forEach(week => {
      const startOfWeek = this.getMondayOfWeek(year, week)
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 4)

      const label = `Semana ${startOfWeek.getDate().toString().padStart(2, '0')}/${(startOfWeek.getMonth()+1).toString().padStart(2, '0')} al ${endOfWeek.getDate().toString().padStart(2, '0')}/${(endOfWeek.getMonth()+1).toString().padStart(2, '0')}`

      const option = document.createElement("option")
      option.value = week
      option.textContent = label

      this.weekSelectorTarget.appendChild(option)
    })

    // If only one week available, auto-trigger update
    if (weeks.length === 1) {
      const event = new Event('change')
      this.weekSelectorTarget.dispatchEvent(event)
    }
  }

  getWeekNumber(date) {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = tempDate.getUTCDay() || 7
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1))
    return Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7)
  }

  getMondayOfWeek(year, week) {
    const simple = new Date(year, 0, 1 + (week - 1) * 7)
    const dow = simple.getDay()
    const monday = new Date(simple)
    if (dow <= 4)
      monday.setDate(simple.getDate() - simple.getDay() + 1)
    else
      monday.setDate(simple.getDate() + 8 - simple.getDay())
    return monday
  }

  // ==============================================
  // MODAL ACTIONS
  // ==============================================

  openModal(event) {
    event.preventDefault()
    event.stopPropagation()

    // First, ensure any existing modal and backdrop are properly cleaned up
    this.closeModal(event)

    // Show loading spinner
    const loadingSpinner = document.getElementById('loading_spinner')
    if (loadingSpinner) {
      loadingSpinner.classList.remove('d-none')
    }

    // Disable all calendar cells to prevent multiple clicks
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      cell.style.pointerEvents = 'none'
    })

    const modalElement = document.getElementById("appointment_modal")
    const date = event.currentTarget.dataset.date
    const hour = event.currentTarget.dataset.hour
    const doctorEmail = event.currentTarget.dataset.doctorEmail
    const calendarId = event.currentTarget.dataset.calendarId

    const url = `/appointments/new?date=${date}&hour=${hour}&doctor_email=${doctorEmail}&calendar_id=${calendarId}`

    this.loadModalContent(url)
  }

  editAppointment(event) {
    event.preventDefault()
    event.stopPropagation()
    const modalElement = document.getElementById("appointment_modal")

    // Check if modal is already shown
    const modalIsShown = modalElement.classList.contains("show")
    if (modalIsShown) {
      return
    }

    // Show loading spinner
    const loadingSpinner = document.getElementById('loading_spinner')
    if (loadingSpinner) {
      loadingSpinner.classList.remove('d-none')
    }

    // Disable all calendar cells to prevent multiple clicks
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      cell.style.pointerEvents = 'none'
    })

    const appointmentId = event.currentTarget.dataset.appointmentId
    const calendarId = event.currentTarget.closest('.calendar-cell').dataset.calendarId
    const doctorEmail = event.currentTarget.closest('.calendar-cell').dataset.doctorEmail

    const url = `/appointments/${appointmentId}/edit?calendar_id=${calendarId}&doctor_email=${doctorEmail}`

    this.loadModalContent(url)
  }

  loadModalContent(url) {
    // Clean up any existing backdrops before loading new content
    const existingBackdrops = document.querySelectorAll('.modal-backdrop')
    existingBackdrops.forEach(backdrop => backdrop.remove())
    document.body.classList.remove('modal-open')
    document.body.style.paddingRight = '0'

    // Clean up previous modal listeners
    this.cleanupModalListeners()

    // Create new abort controller for this fetch
    this.abortController = new AbortController()

    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal: this.abortController.signal
    })
      .then(response => response.text())
      .then(html => {
        // Insert content into modal
        const modalContent = document.getElementById("modal_content")
        modalContent.innerHTML = html

        // Handle collapsible section toggles
        const collapseButtons = modalContent.querySelectorAll('[data-toggle="collapse"]')
        collapseButtons.forEach(button => {
          const targetId = button.getAttribute('data-target')
          const content = targetId ? document.querySelector(targetId) : null

          if (content) {
            button.removeAttribute('data-toggle')
            button.removeAttribute('data-target')
            button.removeAttribute('aria-expanded')
            button.removeAttribute('aria-controls')

            content.style.display = 'none'

            const clickHandler = () => {
              const isVisible = content.style.display !== 'none'
              content.style.display = isVisible ? 'none' : 'block'

              const icon = button.querySelector('i')
              if (icon) {
                icon.classList.toggle('fa-plus-circle')
                icon.classList.toggle('fa-minus-circle')
              }

              if (targetId === '#moreInfo') {
                const buttonText = button.querySelector('span')
                if (buttonText) {
                  buttonText.textContent = isVisible ? 'Mas informacion' : 'Menos informacion'
                }
              }
            }

            button.addEventListener('click', clickHandler)
            // Track listener for cleanup
            this.modalListeners.push({ element: button, event: 'click', handler: clickHandler })
          }
        })

        const modalElement = document.getElementById("appointment_modal")

        // Update header with initial date/time
        const dateField = document.querySelector('[data-target="appointments.dateField"]')
        const startHour = document.querySelector('[data-target="appointments.startHour"]')
        const startMinute = document.querySelector('[data-target="appointments.startMinute"]')

        if (dateField && startHour && startMinute) {
          const dateObj = new Date(dateField.value)
          const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado']
          const dayName = dayNames[dateObj.getDay()]
          const day = dateObj.getDate()
          const time = `${startHour.value}:${startMinute.value}`

          const headerDateTime = document.querySelector('[data-target="appointments.headerDateTime"]')
          if (headerDateTime) {
            headerDateTime.textContent = `${dayName} ${day} - ${time}hs`
          }
        }

        // Create new modal instance
        try {
          const existingModal = bootstrap.Modal.getInstance(modalElement)
          if (existingModal) {
            existingModal.dispose()
          }

          const modal = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          })

          modal.show()

          document.body.style.paddingRight = '0'
        } catch (e) {
          // Fallback to manual approach if Bootstrap fails
          modalElement.style.display = 'block'
          modalElement.classList.add('show')
          modalElement.setAttribute('aria-hidden', 'false')
          document.body.classList.add('modal-open')
          document.body.style.paddingRight = '0'

          if (!document.querySelector('.modal-backdrop')) {
            const backdrop = document.createElement('div')
            backdrop.classList.add('modal-backdrop', 'fade', 'show')
            document.body.appendChild(backdrop)
          }
        }

        // Hide loading spinner and re-enable calendar cells
        const loadingSpinner = document.getElementById('loading_spinner')
        if (loadingSpinner) {
          loadingSpinner.classList.add('d-none')
        }
        const calendarCells = document.querySelectorAll('.calendar-cell')
        calendarCells.forEach(cell => {
          cell.style.pointerEvents = 'auto'
        })

        // Initialize location fields for dynamically loaded content
        this.initializeLocationFields()

        // Add event listeners for modal closing
        this.addModalCloseListeners()
      })
      .catch(error => {
        console.error("Error al cargar modal:", error)
        // Hide loading spinner and re-enable calendar cells even on error
        const loadingSpinner = document.getElementById('loading_spinner')
        if (loadingSpinner) {
          loadingSpinner.classList.add('d-none')
        }
        const calendarCells = document.querySelectorAll('.calendar-cell')
        calendarCells.forEach(cell => {
          cell.style.pointerEvents = 'auto'
        })
      })
  }

  closeModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const modalElement = document.getElementById("appointment_modal")

    try {
      const modal = bootstrap.Modal.getInstance(modalElement)
      if (modal) {
        modal.hide()
      }
    } catch (e) {
      // Fallback if Bootstrap fails
    }

    // Ensure modal is closed
    modalElement.classList.remove('show')
    modalElement.setAttribute('aria-hidden', 'true')
    modalElement.style.display = 'none'

    // Remove all backdrops
    const backdrops = document.querySelectorAll('.modal-backdrop')
    backdrops.forEach(backdrop => backdrop.remove())

    // Restore body scroll
    document.body.classList.remove('modal-open')
    document.body.style.overflow = ''
    document.body.style.paddingRight = '0'

    // Only cleanup URL parameters if this is a real close action
    if (!event || !event.currentTarget || !event.currentTarget.dataset.date) {
      this.cleanupUrlParameters()
    }
  }

  cleanupUrlParameters() {
    const currentUrl = new URL(window.location)
    const params = currentUrl.searchParams

    const essentialParams = ['date', 'doctor_email', 'calendar_id']
    const paramsToRemove = []

    for (const [key, value] of params.entries()) {
      if (!essentialParams.includes(key)) {
        paramsToRemove.push(key)
      }
    }

    paramsToRemove.forEach(param => {
      params.delete(param)
    })

    const newUrl = currentUrl.pathname + (params.toString() ? '?' + params.toString() : '')
    window.history.replaceState({}, '', newUrl)
  }

  addModalCloseListeners() {
    const modalElement = document.getElementById("appointment_modal")
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.cleanupUrlParameters()
      })

      modalElement.addEventListener('click', (event) => {
        if (event.target === modalElement) {
          setTimeout(() => {
            this.cleanupUrlParameters()
          }, 100)
        }
      })

      modalElement.addEventListener('click', (event) => {
        const target = event.target
        if (target.matches('.btn-close, .close, [data-dismiss="modal"], [data-bs-dismiss="modal"]') ||
            target.closest('.btn-close, .close, [data-dismiss="modal"], [data-bs-dismiss="modal"]')) {
          setTimeout(() => {
            this.cleanupUrlParameters()
          }, 100)
        }
      })
    }
  }

  // ==============================================
  // ADD BUTTONS IN FREE SPACES
  // ==============================================

  initializeAddButtons() {
    const calendarCells = document.querySelectorAll('.calendar-cell')
    calendarCells.forEach(cell => {
      // Clean up existing free spaces
      const existingFreeSpaces = cell.querySelectorAll('.free-space')
      existingFreeSpaces.forEach(space => space.remove())

      const appointments = cell.querySelectorAll('.appointment-badge')
      const currentHour = parseInt(cell.dataset.hour)
      const currentDate = cell.dataset.date

      // If no appointments in this cell, don't create free spaces
      // because the main add button is already shown
      if (appointments.length === 0) {
        return
      }

      // Get occupied slots in this cell
      const occupiedSlots = []

      appointments.forEach(apt => {
        const top = parseFloat(apt.style.top) || 0
        const height = parseFloat(apt.style.height) || 100

        if (!isNaN(top) && !isNaN(height) && height > 0) {
          occupiedSlots.push({
            start: top,
            end: top + height,
            source: 'current-cell'
          })
        }
      })

      // Look for events from previous hour that overflow
      const previousHourCell = document.querySelector(`.calendar-cell[data-hour="${currentHour - 1}"][data-date="${currentDate}"]`)
      if (previousHourCell) {
        const previousAppointments = previousHourCell.querySelectorAll('.appointment-badge')
        previousAppointments.forEach(apt => {
          const prevTop = parseFloat(apt.style.top) || 0
          const prevHeight = parseFloat(apt.style.height) || 100

          if (prevTop + prevHeight > 100) {
            const overflowAmount = (prevTop + prevHeight) - 100

            occupiedSlots.push({
              start: 0,
              end: overflowAmount,
              source: 'previous-overflow'
            })
          }
        })
      }

      // Sort slots by start position
      occupiedSlots.sort((a, b) => a.start - b.start)

      // Merge overlapping slots with tolerance
      const mergedSlots = []

      occupiedSlots.forEach(slot => {
        if (mergedSlots.length === 0) {
          mergedSlots.push(slot)
        } else {
          const lastSlot = mergedSlots[mergedSlots.length - 1]
          if (slot.start <= lastSlot.end + this.constructor.SLOT_MERGE_TOLERANCE) {
            lastSlot.end = Math.max(lastSlot.end, slot.end)
          } else {
            mergedSlots.push(slot)
          }
        }
      })

      // Find free spaces
      const freeSpaces = []
      let currentPosition = 0

      mergedSlots.forEach(slot => {
        if (slot.start > currentPosition) {
          const spaceHeight = slot.start - currentPosition
          if (spaceHeight >= this.constructor.MIN_FREE_SPACE_HEIGHT) {
            freeSpaces.push({
              start: currentPosition,
              end: slot.start
            })
          }
        }
        currentPosition = Math.max(currentPosition, slot.end)
      })

      // Add final free space if exists
      if (currentPosition < 100) {
        const finalSpaceHeight = 100 - currentPosition
        if (finalSpaceHeight >= this.constructor.MIN_FREE_SPACE_HEIGHT) {
          freeSpaces.push({
            start: currentPosition,
            end: 100
          })
        }
      }

      // Create free space areas with add buttons
      freeSpaces.forEach(space => {
        const spaceHeight = space.end - space.start

        if (spaceHeight >= this.constructor.MIN_INTERACTIVE_SPACE_HEIGHT) {
          const freeSpace = document.createElement('div')
          freeSpace.className = 'free-space'
          freeSpace.style.top = `${space.start}%`
          freeSpace.style.height = `${spaceHeight}%`
          freeSpace.style.position = 'absolute'
          freeSpace.style.width = '100%'
          freeSpace.style.left = '0'
          freeSpace.style.zIndex = '0'

          const addButton = document.createElement('div')
          addButton.className = 'add-appointment-btn'
          addButton.innerHTML = '<i class="fas fa-plus"></i>'
          addButton.style.position = 'absolute'
          addButton.style.top = '50%'
          addButton.style.left = '50%'
          addButton.style.transform = 'translate(-50%, -50%)'

          // Copy data attributes from cell to button
          const cellData = cell.dataset
          Object.keys(cellData).forEach(key => {
            addButton.dataset[key] = cellData[key]
          })

          // Calculate specific hour based on free space start position
          const minuteOffset = Math.round((space.start / 100) * 60)
          const hour = String(currentHour).padStart(2, '0')
          const minute = String(minuteOffset).padStart(2, '0')
          addButton.dataset.hour = `${hour}:${minute}`

          addButton.addEventListener('click', (e) => {
            e.stopPropagation()
            this.openModal({
              currentTarget: addButton,
              preventDefault: () => {},
              stopPropagation: () => {}
            })
          })

          freeSpace.appendChild(addButton)
          cell.appendChild(freeSpace)
        }
      })
    })
  }

  // ==============================================
  // AUTO-OPEN MODALS
  // ==============================================

  autoOpenModalFromPatientCreation() {
    const autoOpenElement = this.element.querySelector('[data-appointment-calendar-auto-open="true"]')

    if (autoOpenElement) {
      const date = autoOpenElement.dataset.appointmentCalendarDate
      const hour = autoOpenElement.dataset.appointmentCalendarHour
      const doctorEmail = autoOpenElement.dataset.appointmentCalendarDoctorEmail
      let calendarId = autoOpenElement.dataset.appointmentCalendarCalendarId
      const selectedPatientId = autoOpenElement.dataset.appointmentCalendarSelectedPatientId

      // Fallback: if calendar_id is empty, use doctor_email as calendar_id
      if (!calendarId || calendarId.trim() === '') {
        calendarId = doctorEmail
      }

      if (date && hour && doctorEmail && calendarId) {
        const url = `/appointments/new?date=${date}&hour=${hour}&doctor_email=${doctorEmail}&calendar_id=${calendarId}&from_patient_creation=true&selected_patient_id=${selectedPatientId}`

        this.loadModalContent(url)

        setTimeout(() => {
          this.handlePatientPreselection()
        }, this.constructor.PATIENT_PRESELECTION_DELAY)
      }
    }
  }

  autoOpenInvoiceModal() {
    const autoOpenInvoiceElement = this.element.querySelector('[data-appointment-calendar-auto-open-invoice="true"]')

    if (autoOpenInvoiceElement) {
      const appointmentId = autoOpenInvoiceElement.dataset.appointmentCalendarAppointmentId
      const doctorEmail = autoOpenInvoiceElement.dataset.appointmentCalendarDoctorEmail
      const calendarId = autoOpenInvoiceElement.dataset.appointmentCalendarCalendarId

      if (appointmentId && doctorEmail && calendarId) {
        const url = `/appointments/${appointmentId}/edit?calendar_id=${calendarId}&doctor_email=${doctorEmail}&show_invoice_modal=true`

        this.loadModalContent(url)
      }
    }
  }

  handlePatientPreselection() {
    if (!this.element.querySelector('[data-patient-search-target]')) {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const selectedPatientId = urlParams.get('selected_patient_id')
    const fromPatientCreation = urlParams.get('from_patient_creation')

    if (selectedPatientId && fromPatientCreation) {
      const doctorEmail = document.querySelector('input[name="doctor_email"]')?.value || ''

      if (doctorEmail) {
        const searchUrl = `/appointments/search_patients?doctor_email=${encodeURIComponent(doctorEmail)}&patient_id=${selectedPatientId}`

        fetch(searchUrl, {
          signal: this.abortController.signal
        })
          .then(response => response.text())
          .then(html => {
            const resultsContainer = document.querySelector('[data-patient-search-target="results"]')
            if (resultsContainer) {
              resultsContainer.innerHTML = html
              resultsContainer.classList.remove('d-none')

              const firstPatientResult = resultsContainer.querySelector('.patient-search-result')
              if (firstPatientResult) {
                firstPatientResult.click()
              }
            } else {
              console.error('Results container not found')
            }
          })
          .catch(error => {
            console.error('Error loading patient:', error)
          })
      }
    }
  }

  // ==============================================
  // HELPER METHODS
  // ==============================================

  cleanupModalListeners() {
    // Remove all tracked event listeners
    if (this.modalListeners && this.modalListeners.length > 0) {
      this.modalListeners.forEach(({ element, event, handler }) => {
        if (element) {
          element.removeEventListener(event, handler)
        }
      })
      this.modalListeners = []
    }
  }

  initializeLocationFields() {
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')
    if (locationInput) {
      try {
        // Find the form controller if available
        const formController = this.application.getControllerForElementAndIdentifier(
          document.querySelector('[data-controller*="appointment-form"]'),
          'appointment-form'
        )
        if (formController) {
          formController.updateLocation({ target: { dataset: { location: locationInput.value } } })
          // Let form controller handle time selector initialization
          formController.initializeTimeSelectors()
        }
      } catch (error) {
        console.warn('Error initializing location fields:', error)
      }
    }
  }
}
